import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Send, Check, Copy, Share2 } from 'lucide-react';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const WaitlistWidget = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [incomingRef, setIncomingRef] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref) setIncomingRef(ref);
    } catch (e) {
      /* noop */
    }
  }, []);

  const shareLink = referralCode
    ? `${window.location.origin}/fan/${referralCode}`
    : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Enter your email to get on the list');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/waitlist`, {
        email: email.trim(),
        name: name.trim(),
        source: 'homepage',
        ref: incomingRef || undefined,
      });
      setReferralCode(data.referral_code || '');
      setJoined(true);
      toast.success(data.message || "You're in!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? 'Please enter a valid email' : (detail || 'Something went wrong. Try again.');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success('Share link copied!');
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error('Copy failed - link:  ' + shareLink);
    }
  };

  return (
    <section
      className="py-16 relative overflow-hidden"
      data-testid="waitlist-section"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 via-black to-cyan-900/20" />
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 30%, rgba(139,92,246,0.4), transparent 50%), radial-gradient(circle at 80% 70%, rgba(6,182,212,0.35), transparent 50%)',
        }}
      />

      <div className="container relative z-10">
        <div className="max-w-3xl mx-auto">
          <div
            className="glass-card p-8 md:p-10 border border-purple-500/20"
            data-testid="waitlist-widget"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-purple-500/40">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-xs uppercase tracking-widest text-cyan-300 font-semibold">
                First-Listen Access
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold mb-3 leading-tight">
              Hear every drop{' '}
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                before anyone else
              </span>
            </h2>
            <p className="text-gray-400 text-base md:text-lg mb-7 max-w-xl">
              Drop your email — I'll ping you the moment a new track goes live, plus early access to
              presave bonuses and merch drops.
            </p>

            {incomingRef && !joined && (
              <div
                className="mb-5 rounded-lg bg-cyan-500/10 border border-cyan-400/30 px-4 py-3 text-sm text-cyan-200"
                data-testid="waitlist-referred-banner"
              >
                <Share2 className="w-4 h-4 inline mr-2" />
                Invited by a friend — you'll help unlock their bonus track when you join.
              </div>
            )}

            {joined ? (
              <div className="space-y-4" data-testid="waitlist-success">
                <div className="flex items-center gap-3 rounded-xl bg-green-500/10 border border-green-400/30 px-5 py-4">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">You're on the list.</p>
                    <p className="text-sm text-gray-400">Check your inbox for a welcome note.</p>
                  </div>
                </div>

                {referralCode && (
                  <div
                    className="rounded-xl bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-purple-400/30 p-5"
                    data-testid="waitlist-referral-block"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <Share2 className="w-5 h-5 text-purple-300 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-white">Unlock a bonus track</p>
                        <p className="text-sm text-gray-400">
                          Bring 3 friends and I'll email you an unreleased bonus. Share your link:
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        readOnly
                        value={shareLink}
                        className="input-dark flex-1 font-mono text-xs sm:text-sm"
                        data-testid="waitlist-share-link"
                        onClick={(e) => e.target.select()}
                      />
                      <button
                        type="button"
                        onClick={handleCopy}
                        className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap"
                        data-testid="waitlist-copy-link-button"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied' : 'Copy Link'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="flex flex-col sm:flex-row gap-3"
                data-testid="waitlist-form"
              >
                <Input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-dark sm:max-w-[200px]"
                  data-testid="waitlist-name-input"
                  disabled={loading}
                />
                <Input
                  type="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-dark flex-1"
                  required
                  data-testid="waitlist-email-input"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-60"
                  data-testid="waitlist-submit-button"
                >
                  <Send className="w-4 h-4" />
                  {loading ? 'Adding you...' : 'Get First Listen'}
                </button>
              </form>
            )}

            <p className="text-xs text-gray-500 mt-4">
              No spam, ever. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
